import { Chart as ChartJS, TooltipLabelStyle, TooltipModel } from "chart.js";

const getOrCreateTooltip = (chart: ChartJS) => {
  const parent = chart?.canvas?.parentNode as HTMLElement | null;
  if (!parent) return null;

  // Ensure the parent is positioned for absolute tooltip placement
  const parentComputed = getComputedStyle(parent);
  if (parentComputed.position === "static") {
    parent.style.position = "relative";
  }

  let tooltipEl = parent.querySelector(
    "div.chartjs-external-tooltip",
  ) as HTMLDivElement | null;

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className = "chartjs-external-tooltip";
    tooltipEl.style.background = "rgba(0, 0, 0, 0.7)";
    tooltipEl.style.borderRadius = "3px";
    tooltipEl.style.color = "white";
    tooltipEl.style.opacity = "0";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.transform = "translate(-50%, 0)";
    tooltipEl.style.transition = "all .1s ease";

    const table = document.createElement("table");
    table.style.margin = "0px";

    tooltipEl.appendChild(table);
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const createTableHead = (
  tableHead: HTMLElement,
  bodyLine: string[],
  colors: TooltipLabelStyle,
) => {
  const div = document.createElement("div");
  div.style.display = "flex";
  div.style.alignItems = "center";
  div.style.justifyContent = "start";

  const span = document.createElement("span");
  span.style.background = colors.backgroundColor.toString();
  span.style.borderColor = colors.borderColor.toString();
  span.style.borderWidth = "2px";
  span.style.marginRight = "10px";
  span.style.minWidth = "10px";
  span.style.maxWidth = "10px";
  span.style.height = "10px";
  span.style.width = "10px";
  span.style.display = "inline-block";

  const text = document.createTextNode(bodyLine[0].split(":")[0]);
  const tr = document.createElement("tr");
  tr.style.borderWidth = "0";

  const th = document.createElement("th");
  th.style.borderWidth = "0";

  div.appendChild(span);
  div.appendChild(text);
  th.appendChild(div);
  tr.appendChild(th);
  tableHead.appendChild(tr);
};

const createTableBody = (
  tableBody: HTMLElement,
  bodyLine: string[],
  titleLine: string,
) => {
  const doseTableRow = document.createElement("tr");
  doseTableRow.style.backgroundColor = "inherit";
  doseTableRow.style.borderWidth = "0";

  const volumeTableRow = document.createElement("tr");
  volumeTableRow.style.backgroundColor = "inherit";
  volumeTableRow.style.borderWidth = "0";

  const volumeText = document.createTextNode(
    "Volume: " + bodyLine[0].split(":")[1],
  );
  const doseText = document.createTextNode("Dose: " + titleLine);

  doseTableRow.appendChild(doseText);
  volumeTableRow.appendChild(volumeText);
  tableBody.appendChild(doseTableRow);
  tableBody.appendChild(volumeTableRow);
};

export const externalTooltipHandler = (context: {
  chart: ChartJS;
  tooltip: TooltipModel<"line">;
}) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);
  if (!tooltipEl) return;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b) => b.lines);

    const tableHead = document.createElement("thead");
    const tableBody = document.createElement("tbody");

    const colors = tooltip.labelColors[0];

    if (titleLines.length > 0 && bodyLines.length > 0) {
      const titleLine = titleLines[0];
      const bodyLine = bodyLines[0];
      createTableHead(tableHead, bodyLine, colors);
      createTableBody(tableBody, bodyLine, titleLine);
    }

    const tableRoot = tooltipEl.querySelector("table");

    // Remove old children
    while (tableRoot?.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot?.appendChild(tableHead);
    tableRoot?.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = "1";
  tooltipEl.style.left = positionX + tooltip.caretX + "px";
  tooltipEl.style.top = positionY + tooltip.caretY + "px";
  tooltipEl.style.padding =
    tooltip.options.padding + "px " + tooltip.options.padding + "px";
};
